import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { Dispatch } from "react";
import { ErrorDetailContainer } from "../api/config";
import {
    ConstructorViewInfoStatus,
    FolderDto,
    NoteDto,
    ProjectDetailDto,
    ProjectService,
    SaleActivityDto,
    SaleDto,
    ViewConstructorResultDto,
} from "../api/psc";
import { getCurrentDate } from "../utils/date";
import {
    addOrUpdateNoteAction,
    addOrUpdateSaleAction,
    addOrUpdateSaleActivityAction,
    deleteActivityAction,
    deleteNoteAction,
    deleteSaleAction,
    projectVisitedAction,
} from "./SharedActions";
import { updateAccess } from "./SharedSlice";

export interface ProjectDetailState {
    projectHashId?: string;
    requirementId?: number;
    projectDetail?: ProjectDetailDto;
    notes: NoteDto[];
    sales: SaleDto[];
    fetchingProjectDetail: boolean;
    fetchingConstructor: boolean;
    showExtraDetail: boolean;
    errorMessage: string | null;
}

const initialState: ProjectDetailState = {
    projectHashId: null,
    requirementId: null,
    projectDetail: null,
    notes: [],
    sales: [],
    fetchingProjectDetail: true,
    fetchingConstructor: false,
    showExtraDetail: false,
    errorMessage: null,
};

const projectDetailSlice = createSlice({
    name: "projectDetail",
    initialState,
    reducers: {
        initializeProjectDetail(
            state: ProjectDetailState,
            action: PayloadAction<{
                projectHashId: string;
                requirementId?: number;
            }>
        ) {
            return {
                ...initialState,
                projectHashId: action.payload.projectHashId,
                requirementId: action.payload.requirementId,
                showExtraDetail: state.showExtraDetail,
            };
        },
        resetProjectDetail(
            state: ProjectDetailState,
            action: PayloadAction<{
                projectDetail: ProjectDetailDto;
                notes: NoteDto[];
                sales: SaleDto[];
            }>
        ) {
            return {
                ...state,
                projectDetail: action.payload.projectDetail,
                notes: action.payload.notes,
                sales: action.payload.sales,
                fetchingProjectDetail: false,
                errorMessage: null,
            };
        },
        viewConstructor(
            state: ProjectDetailState,
            action: PayloadAction<ViewConstructorResultDto>
        ) {
            const visitDate = getCurrentDate();
            return {
                ...state,
                projectDetail: {
                    ...state.projectDetail,
                    constructorViewInfo: action.payload,
                    address: action.payload.address,
                    visitDate: visitDate.toJSON(),
                    isVisited:
                        action.payload.status ===
                        ConstructorViewInfoStatus.NO_ERROR,
                },
                fetchingConstructor: false,
                errorMessage: null,
            };
        },
        setProjectDetailFolders(
            state: ProjectDetailState,
            action: PayloadAction<FolderDto[]>
        ) {
            return {
                ...state,
                projectDetail: {
                    ...state.projectDetail,
                    folders: action.payload,
                },
            };
        },
        setFetchingConstructorLoadingStatus(
            state: ProjectDetailState,
            action: PayloadAction<boolean>
        ) {
            return { ...state, fetchingConstructor: action.payload };
        },
        setProjectDetailVisibility(
            state: ProjectDetailState,
            action: PayloadAction<boolean>
        ) {
            return {
                ...state,
                projectDetail: {
                    ...state.projectDetail,
                    isHidden: action.payload,
                },
            };
        },
        showExtraDetails(state: ProjectDetailState) {
            return { ...state, showExtraDetail: true };
        },
        setErrorMessage(
            state: ProjectDetailState,
            action: PayloadAction<string | null>
        ) {
            return {...state, errorMessage: action.payload}
        }
    },
    extraReducers: (builder) => {
        builder.addCase(
            addOrUpdateSaleAction,
            (state: ProjectDetailState, action: PayloadAction<SaleDto>) => {
                if (!state.projectDetail) {
                    return;
                }

                const saleToUpdate = state.sales.find(
                    (f) => f.id === action.payload.id
                );
                let updatedSale = [...state.sales];

                if (saleToUpdate) {
                    const indexOfNote = state.sales.indexOf(saleToUpdate);
                    updatedSale[indexOfNote] = action.payload;
                } else {
                    updatedSale = [...state.sales, action.payload];
                }

                return {
                    ...state,
                    sales: updatedSale,
                };
            }
        );

        builder.addCase(
            addOrUpdateSaleActivityAction,
            (
                state: ProjectDetailState,
                action: PayloadAction<SaleActivityDto>
            ) => {
                if (!state.projectDetail) {
                    return;
                }

                let updatedSales = [...state.sales];
                const sale = updatedSales.find(
                    (f) => f.id === action.payload.saleId
                );

                if (sale) {
                    const updatedActivities = [...sale.activities];
                    const activityToBeUpdated = updatedActivities.find(
                        (a) => a.id === action.payload.id
                    );
                    const index =
                        updatedActivities.indexOf(activityToBeUpdated);

                    if (index === -1) {
                        updatedActivities.push(action.payload);
                    } else {
                        updatedActivities[index] = action.payload;
                    }

                    const saleIndex = updatedSales.indexOf(sale);
                    updatedSales[saleIndex] = {
                        ...sale,
                        activities: updatedActivities,
                    };
                }

                return {
                    ...state,
                    sales: updatedSales,
                };
            }
        );

        builder.addCase(
            deleteActivityAction,
            (state: ProjectDetailState, action: PayloadAction<number>) => {
                if (!state.projectDetail) {
                    return;
                }

                let updatedSales = [...state.sales];

                for (const sale of updatedSales) {
                    for (const activity of sale.activities) {
                        if (activity.id === action.payload) {
                            const updatedActivities = [
                                ...sale.activities.filter(
                                    (a) => a.id !== action.payload
                                ),
                            ];
                            const saleIndex = updatedSales.indexOf(sale);
                            updatedSales[saleIndex] = {
                                ...sale,
                                activities: updatedActivities,
                            };
                        }
                    }
                }

                return {
                    ...state,
                    sales: updatedSales,
                };
            }
        );

        builder.addCase(
            deleteSaleAction,
            (state: ProjectDetailState, action: PayloadAction<number>) => {
                if (!state.projectDetail) {
                    return;
                }

                let updatesSales = [
                    ...state.sales.filter((n) => n.id !== action.payload),
                ];

                return {
                    ...state,
                    sales: updatesSales,
                };
            }
        );

        builder.addCase(
            addOrUpdateNoteAction,
            (state: ProjectDetailState, action: PayloadAction<NoteDto>) => {
                if (!state.projectDetail) {
                    return;
                }

                let updatedNotes = [...state.notes];
                const note = updatedNotes.find(
                    (n) => n.id === action.payload.id
                );

                if (note) {
                    const index = updatedNotes.indexOf(note);
                    updatedNotes[index] = action.payload;
                } else {
                    updatedNotes.push(action.payload);
                }

                return {
                    ...state,
                    notes: updatedNotes,
                };
            }
        );

        builder.addCase(
            deleteNoteAction,
            (state: ProjectDetailState, action: PayloadAction<number>) => {
                if (!state.projectDetail) {
                    return;
                }

                let updatedNotes = [
                    ...state.notes.filter((n) => n.id !== action.payload),
                ];

                return {
                    ...state,
                    notes: updatedNotes,
                };
            }
        );
    },
});

export function fetchProjectDetail(
    state: ProjectDetailState,
    projectHashId: string,
    requirementId: number | null,
    dispatch: Dispatch<any>,
    onResult: (success: boolean) => void
) {
    dispatch(initializeProjectDetail({ projectHashId, requirementId }));
    Promise.all([
        ProjectService.getProjectDetail(projectHashId, "1", requirementId),
        ProjectService.getProjectNotes(projectHashId, "1"),
        ProjectService.getProjectSales(projectHashId, "1"),
    ])
        .then((p) => {
            dispatch(
                resetProjectDetail({
                    projectDetail: p[0],
                    notes: p[1],
                    sales: p[2],
                })
            );
            onResult(true);
        })
        .catch((e) => {
            onResult(false);
        });
}

export function fetchConstructor(
    state: ProjectDetailState,
    dispatch: Dispatch<any>
) {
    dispatch(setErrorMessage(null));
    dispatch(setFetchingConstructorLoadingStatus(true));

    const request = state.requirementId
        ? ProjectService.viewProjectConstructorWithRequirement(
              state.requirementId,
              "1"
          )
        : ProjectService.viewProjectConstructor(state.projectHashId, "1");

    request
        .then((c) => {
            dispatch(viewConstructor(c));

            if (c.status === ConstructorViewInfoStatus.NO_ERROR) {
                dispatch(projectVisitedAction(state.projectHashId));
                dispatch(updateAccess(c.updatedAccess));
            }
        })
        .catch((e: ErrorDetailContainer) => {
            dispatch(setFetchingConstructorLoadingStatus(false));
            dispatch(setErrorMessage(e.errors.at(0)?.message))
        });
}

export const {
    initializeProjectDetail,
    resetProjectDetail,
    viewConstructor,
    setFetchingConstructorLoadingStatus,
    setProjectDetailFolders,
    setProjectDetailVisibility,
    showExtraDetails,
    setErrorMessage
} = projectDetailSlice.actions;

export default projectDetailSlice.reducer;
